import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Truck as TruckIcon, Users, MapPin } from "lucide-react";
import { FollowJourneyPill } from "@/components/FollowJourneyPill";
import { WatchStoryDonut } from "@/components/WatchStoryDonut";
import { Mission } from "@/components/Mission";

const PARAGRAPHS = [
  "As a family, we knew that it would be difficult for Zach to get a job someday. We read the statistics, that Deaf men of color, particularly if they had additional disabilities were significantly less likely to find a job than their hearing peers. Statistics like this kept us up at night, worrying about what the future would look like for our vibrant, social son.",
  "We began to dream about creating something of our own, that could not only help our son, but other adults with differing abilities. My husband is a numbers guy, by trade, and he ran some numbers, and felt that a donut business would be a viable option, and of course, there isn't one currently in Mattawan. More numbers were crunched, and we decided to start with a donut truck for many factors.",
  "Our goals are to fully become an inclusive community that embraces and values everyone's strengths and gifts that they bring to the table, AND to teach our community ASL through our videos online, and continued interactions in the community.",
  "Zach IS Bobo, it's a nickname from childhood that has stuck. All 7 of our kids all help on the donut truck with the exception of our youngest, but you will see her around. They are Zach's biggest cheerleaders and it has been beautiful to see them rallying around their brother to build something beautiful for his future.",
  "Zach is our donut cutter extraordinaire, so he's usually in the back of the truck, but he loves to say hi, so if you come to our window, don't be afraid to ask for him! He will pop his head out to say hi and give you his iconic smile.",
  "We are forever grateful for our amazing community that has loved and supported us in this endeavor!",
];

const POINTS = [
  { icon: TruckIcon, title: "Rolling kitchen", text: "We crunched the numbers and a donut truck just made sense — so we hit the road." },
  { icon: Users, title: "All hands on deck", text: "All six of our other kids also pitch in. (Except our youngest, because she's only 4!) Together, they're Zach's biggest cheerleaders." },
  { icon: MapPin, title: "Made in Mattawan", text: "Proudly serving Mattawan, Paw Paw, Lawrence and beyond, week after week." },
];

export const OurStory = () => {
  return (
    <>
      <section data-testid="our-story-page" className="pt-28 md:pt-36 pb-20 md:pb-32 px-5 md:px-10 woodgrain">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* LEFT — back pill, heading, story text, follow pill */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:-mt-6"
          >
            <div className="flex items-end gap-3">
              <img
                src="/logo-truck.png?v=3"
                alt="Bobo's Donuts truck"
                data-testid="about-back-truck"
                className="h-[8.19rem] w-auto object-contain select-none translate-y-2 drop-shadow-[0_6px_10px_rgba(62,39,35,0.18)]"
              />
              <Link
                to="/"
                data-testid="our-story-back-link"
                className="inline-flex items-center gap-2 px-7 py-3 sm:py-4 rounded-full bg-white/70 border-2 border-[#111111]/15 font-bold text-lg text-[#111111] hover:bg-white hover:scale-105 transition-all shadow-lg"
              >
                <ArrowLeft className="h-5 w-5" /> Back to the donuts
              </Link>
            </div>

            <h1
              data-testid="our-story-heading"
              className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-[#111111] tracking-tight mt-8"
            >
              Our Story
            </h1>

            <div data-testid="our-story-body" className="mt-8 space-y-6">
              {PARAGRAPHS.map((p, i) => (
                <p
                  key={p}
                  data-testid={`our-story-paragraph-${i}`}
                  className="text-lg text-[#3d3d3d] leading-relaxed"
                >
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-10">
              <FollowJourneyPill />
            </div>
          </motion.div>

          {/* RIGHT — photo, story points, watch-our-story donut */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="space-y-12"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-[#ff648b]/40 rounded-[2.5rem] rotate-3" />
              <img
                src="/our-story-zach.jpg"
                alt='A young man in a pink tie-dye T-shirt smiling and making a hand gesture, standing in front of a black food truck with a sign that says "Bobo&apos;s Donuts" and features a cartoon donut on a van.'
                data-testid="our-story-photo"
                className="relative w-full h-auto rounded-3xl shadow-2xl ring-4 ring-white/60 object-cover select-none"
              />
            </div>

            <div className="space-y-5">
              {POINTS.map((p) => (
                <div key={p.title} className="flex gap-4 items-start">
                  <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#ff648b] flex items-center justify-center shadow">
                    <p.icon className="h-6 w-6 text-[#111111]" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-xl text-[#111111]">{p.title}</h3>
                    <p className="text-[#3d3d3d]">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-2">
              <WatchStoryDonut />
            </div>
          </motion.div>
        </div>
      </section>

      <Mission showLearnMore={false} showVideo={false} showSocial={false} />
    </>
  );
};
